import * as React from 'react';
import { FunctionComponent, createContext, useState, useEffect } from 'react';

import { StudentsNavigation } from 'features/primaryNavigation';
import { PageWithoutSidebar } from 'features/layouts';
import { Nav } from 'basecamp';
import { FilterBarOverview, FilterBarPM, FilterBarP8 } from './components';

import { fetchStudentViewGroupsThunk, fetchPriorAttainmentTypesThunk } from './redux';
import {
	getAppliedStudentFilters,
	setAppliedComparisons,
	setAppliedFilters,
	getAppliedFilters,
	getAppliedComparisons,
	getAppliedGradepointKeys,
} from '../app/redux/context';

import { getAppliedStudentComparisons } from '../../utils/selectors/comparisonSelectors';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import GetRoutes from '../../routes';

import PageTitle from 'features/common/ui/pageTitle';

type Props = {
	routes: Routes.Route[];
};

// @ts-ignore
export const SPOContext = createContext<SPO.Context>();

const SPO: FunctionComponent<Props> = (props) => {
	const dispatch = useDispatch();
	const history = useHistory();
	/**
	 ** Handle State
	 */
	const [search, setSearch] = useState<string>('');
	const [showCAT4, setShowCAT4] = useState<boolean>(false);
	const [comparisonGp, setComparisonGp] = useState<SPO.Option[]>([]);
	const [focusOn, setFocusOn] = useState<SPO.Option | undefined>(undefined);
	const [togglePts, setTogglePts] = useState<boolean>(false);
	const [showStatusToggle, setShowStatusToggle] = useState<boolean>(true);

	const filters = useSelector(getAppliedStudentFilters);
	const comparisons = useSelector(getAppliedStudentComparisons);

	const { tutorGroup, studentPage } = useParams<{
		tutorGroup: string | undefined;
		studentPage: string | undefined;
	}>();
	const setTutorGroup = (value: string | undefined) => {
		const route = value ? `/students/tutor-group/${encodeURIComponent(value)}` : '/students';
		history.push(route);
	};

	/**
	 * Pass state values via context
	 * Values are available to all page components
	 */
	const SPOContextValues: SPO.Context = {
		search,
		setSearch,
		showCAT4,
		setShowCAT4,
		showStatusToggle,
		setShowStatusToggle,
		togglePts,
		setTogglePts,
		filters,
		setFilters: setAppliedFilters,
		comparisons,
		setComparisons: setAppliedComparisons,
		comparisonGp,
		setComparisonGp,
		focusOn,
		setFocusOn,
		tutorGroup,
		setTutorGroup,
	};

	const allFilters = useSelector(getAppliedFilters);
	const allComparisons = useSelector(getAppliedComparisons);
	const gradepoints = useSelector(getAppliedGradepointKeys);

	/**
	 * Handle lifecycle updates
	 */

	useEffect(() => {
		dispatch(fetchPriorAttainmentTypesThunk());
	}, []);

	useEffect(() => {
		dispatch(
			fetchStudentViewGroupsThunk({
				tutorGroup: tutorGroup ? decodeURIComponent(tutorGroup) : undefined,
			})
		);
	}, [allComparisons, gradepoints, allFilters, tutorGroup]);

	/**
	 * Render filter bar
	 */
	const renderFilterBar = () => {
		switch (studentPage) {
			case 'performance-measures':
				return <FilterBarPM />;
			case 'progress8':
				return <FilterBarP8 />;
			default:
				return <FilterBarOverview />;
		}
	};

	/**
	 * Renders the primary navigation component for Student Performance Overview (SPO)
	 * Contains links to different student views and sections
	 * Positioned relatively with z-index to ensure proper stacking context
	 */
	const spoNavigation = () => {
		return (
			<Nav mx={4} position="relative" zIndex={1998}>
				<StudentsNavigation />
			</Nav>
		);
	};

	/**
	 ** Render Component
	 */
	return (
		<SPOContext.Provider value={SPOContextValues}>
			<PageWithoutSidebar
				dataTest="spo"
				headerElements={[<PageTitle routes={props.routes} />, spoNavigation(), renderFilterBar()]}
			>
				<GetRoutes routes={props.routes} />
			</PageWithoutSidebar>
		</SPOContext.Provider>
	);
};

export default SPO;
